/**
 * 前端 API 根路径拼装。环境变量约定见 `fe/data-fe/.env.production` 文件头：
 * - `*_PATH_PREFIX`：同域路径，以 `/` 开头（无 scheme）
 * - `*_ORIGIN`：完整 Origin，以 `http(s)://` 开头（可跨域，需 CORS）
 */

const normalizeBase = (value: string | undefined, fallback: string): string =>
  (value || fallback).replace(/\/+$/, '');

const parseTimeoutMs = (value: string | undefined, fallback: number): number => {
  const parsed = Number(value);
  return Number.isFinite(parsed) && parsed > 0 ? Math.floor(parsed) : fallback;
};

const TEN_MINUTES_MS = 10 * 60 * 1000;

export const API_CONFIG = {
  AUTH_PATH_PREFIX: normalizeBase(import.meta.env.VITE_AUTH_PATH_PREFIX, '/auth'),
  GOOGLE_CLIENT_ID: import.meta.env.VITE_GOOGLE_CLIENT_ID || '',
  MANUAL_TRADING_ORIGIN: normalizeBase(import.meta.env.VITE_MANUAL_TRADING_ORIGIN, ''),
  ENGINE_READ_ORIGIN: normalizeBase(
    import.meta.env.VITE_ENGINE_READ_ORIGIN,
    'https://trading.arbx.ai'
  ),
  SPREAD_PATH_PREFIX: normalizeBase(import.meta.env.VITE_SPREAD_PATH_PREFIX, '/api/server-py'),
  CANDLE_PATH_PREFIX: normalizeBase(import.meta.env.VITE_CANDLE_PATH_PREFIX, '/api/server-py'),
  INSTRUMENTS_PATH_PREFIX: normalizeBase(import.meta.env.VITE_INSTRUMENTS_PATH_PREFIX, '/api/instruments'),
  PREMIUM_INDEX_PATH_PREFIX: normalizeBase(
    import.meta.env.VITE_PREMIUM_INDEX_PATH_PREFIX,
    '/api/premium-index'
  ),
  MARK_PRICE_PATH_PREFIX: normalizeBase(
    import.meta.env.VITE_MARK_PRICE_PATH_PREFIX,
    '/api/mark-price'
  ),
  INDEX_PRICE_PATH_PREFIX: normalizeBase(
    import.meta.env.VITE_INDEX_PRICE_PATH_PREFIX,
    '/api/index-price'
  ),
  FUNDING_RATES_PATH_PREFIX: normalizeBase(
    import.meta.env.VITE_FUNDING_RATES_PATH_PREFIX,
    '/api/funding-collector'
  ),
  OPEN_INTEREST_PATH_PREFIX: normalizeBase(
    import.meta.env.VITE_OPEN_INTEREST_PATH_PREFIX,
    '/api/open-interest'
  ),
  TRADING_VOLUME_PATH_PREFIX: normalizeBase(
    import.meta.env.VITE_TRADING_VOLUME_PATH_PREFIX,
    '/api/trading-volume'
  ),
  LOAN_INFO_PATH_PREFIX: normalizeBase(import.meta.env.VITE_LOAN_INFO_PATH_PREFIX, '/api/loan-info'),
  DEPOSIT_WITHDRAW_PATH_PREFIX: normalizeBase(
    import.meta.env.VITE_DEPOSIT_WITHDRAW_PATH_PREFIX,
    '/api/deposit-withdraw'
  ),
  /** 空字符串表示同域 `/api/opp-detector/*` */
  OPP_DETECTOR_PATH_PREFIX: normalizeBase(import.meta.env.VITE_OPP_DETECTOR_PATH_PREFIX, ''),
  /** `apiFetch` 默认请求超时（毫秒） */
  API_FETCH_TIMEOUT_MS: parseTimeoutMs(import.meta.env.VITE_API_FETCH_TIMEOUT_MS, TEN_MINUTES_MS),
} as const;

/** 已配置 trading-engine API 根（仅交易接口，与 auth-service 分离） */
export function hasManualTradingOrigin(): boolean {
  return API_CONFIG.MANUAL_TRADING_ORIGIN.length > 0;
}

export function hasGoogleClientId(): boolean {
  return API_CONFIG.GOOGLE_CLIENT_ID.trim().length > 0;
}

/** 浏览器请求 trading-engine 的 URL 根（可与本站同域，如 `/api/trading-engine`） */
export function resolvedManualTradingOrigin(): string {
  return hasManualTradingOrigin() ? API_CONFIG.MANUAL_TRADING_ORIGIN : '';
}

/** 统一认证中心 `data/auth-service`（同域一般为 `/auth`） */
export function resolvedAuthOrigin(): string {
  return API_CONFIG.AUTH_PATH_PREFIX;
}

/** `me`, `login`, `logout`, `refresh`, `register` — segment 无斜杠前缀 */
export function authUrl(segment: string): string {
  const base = resolvedAuthOrigin();
  return `${base}/${segment}`;
}

/** 将 HTTP API 根转为 WebSocket 根（支持绝对 URL 与同域路径前缀） */
export function toWsBase(httpBase: string): string {
  if (!httpBase) {
    if (typeof window === 'undefined') return '';
    const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
    return `${protocol}//${window.location.host}`;
  }
  if (httpBase.startsWith('http://')) return `ws://${httpBase.slice('http://'.length)}`;
  if (httpBase.startsWith('https://')) return `wss://${httpBase.slice('https://'.length)}`;
  if (httpBase.startsWith('/')) {
    if (typeof window === 'undefined') return httpBase;
    const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
    return `${protocol}//${window.location.host}${httpBase}`;
  }
  return httpBase;
}

export function manualTradingApiUrl(path: string): string {
  const base = resolvedManualTradingOrigin();
  const p = path.startsWith('/') ? path : `/${path}`;
  return `${base}${p}`;
}
